import React, { useState } from "react";
// ui
import Button from "@mui/material/Button";
import LoadingButton from "@mui/lab/LoadingButton";
import Badge from "@mui/material/Badge";
// style
import { createStyles, makeStyles, styled } from "@material-ui/styles";
import PropTypes from "prop-types";
import classNames from "classnames";
import Typography from "@mui/material/Typography";
// config

// useStyle
const useStyles = makeStyles((theme) =>
  createStyles({
    root: {},
  })
);

const basicButtonProps = {
  padding: "9px 12px",
  borderRadius: "8px",
};

const StyledButtonClear = styled(Button)(({ theme }) => ({
  ...basicButtonProps,
  "&.MuiButton-root": {
    border: "1px solid #3D3D3D",
    backgroundColor: "transparent",
    color: "#fff",
  },
}));

const StyledButtonContained = styled(Button)(({ theme }) => ({
  ...basicButtonProps,
  boxShadow: "0 8px 16px 0 rgba(59, 191, 240, 0.16)",
  "&.MuiButton-root": {
    backgroundColor: "#3bbff0",
    color: "#fff",
    boxShadow: "0 8px 16px 0 rgba(59, 191, 240, 0.16)",
    "&:hover": {
      backgroundColor: "#06a2e6",
      color: "#fff",
    },
  },
  "&.MuiButton-contained": {},
}));

const StyledButtonGradient = styled(Button)(({ theme }) => ({
  ...basicButtonProps,
  "&.MuiButton-root": {
    backgroundImage: "linear-gradient(to bottom, #3bbff0, #06a2e6)",
    color: "#fff",
    "&:hover": {
      backgroundImage: "linear-gradient(to bottom, #06a2e6, #06a2e6)",
    },
  },
}));

const StyledButtonNeutral = styled(Button)(({ theme }) => ({
  ...basicButtonProps,
  "&.MuiButton-root": {
    backgroundColor: "#e6e9ef",
    color: "#121212",
    "&:hover": {
      backgroundColor: "#c9c9c9",
    },
  },
}));

const StyledButtonNeutralNoHover = styled(Button)(({ theme }) => ({
  ...basicButtonProps,
  "&.MuiButton-root": {
    backgroundColor: "#F6F6F6",
    color: "#5D5D5D",
    "&:hover": {
      backgroundColor: "#F6F6F6",
    },
  },
}));

const StyledButtonOutlined = styled(Button)(({ theme }) => ({
  ...basicButtonProps,
  "&.MuiButton-root": {
    border: "solid 1px #e6e9ef",
    backgroundColor: "#fff",
    color: "#121212",
    "&:hover": {
      backgroundColor: "#e6e9ef",
    },
  },
}));

const StyledButtonText = styled(Button)(({ theme }) => ({
  ...basicButtonProps,
  "&.MuiButton-root": {
    backgroundColor: "#fff",
    color: "#121212",
    "&:hover": {
      backgroundColor: "#f7f8fa",
    },
  },
}));

const StyledButtonHoverText = styled(Button)(({ theme }) => ({
  ...basicButtonProps,
  "&.MuiButton-root": {
    backgroundColor: "#fff",
    color: "#121212",
    "&:hover": {
      backgroundColor: "#3BBFF01A",
      color: "#0EA2E9",
    },
  },
}));

const StyledButtonDisabled = styled(Button)(({ theme }) => ({
  ...basicButtonProps,
  "&.MuiButton-root": {
    backgroundColor: "#e6e9ef",
    color: "#c9c9c9",
  },
}));

const StyledButtonFeedback = styled(Button)(({ theme }) => ({
  ...basicButtonProps,
  "&.MuiButton-root": {
    border: "1px #E7E7E7 solid",
    backgroundColor: "#ffffff",
    color: "#121212",
    "&:hover": {
      backgroundColor: "rgba(59, 191, 240, 0.1)",
    },
  },
}));

const StyledButtonDisabledDark = styled(Button)(({ theme }) => ({
  ...basicButtonProps,
  "&.MuiButton-root": {
    backgroundColor: "#505050",
    color: "#909090",
  },
}));

const StyledButtonLoading = styled(LoadingButton)(({ theme }) => ({
  ...basicButtonProps,
  padding: "9px 12px",
  borderRadius: "8px",
  "&.MuiButton-root": {
    backgroundColor: "#e6e9ef",
    color: "#c9c9c9",
  },
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: "#ffb629",
    color: "#fff",
  },
}));

// FIXME:: new button style - 20240429
const StyledButtonLinearGradient = styled(Button)(({ theme }) => ({
  ...basicButtonProps,
  "&.MuiButton-root": {
    textWrap: "nowrap",
    background: "linear-gradient(90deg, #38BBF8 0%, #0EA2E9 100%)",
    boxShadow: "0px 0px 16px 0px #3BBFF033",
    borderRadius: "6px",
    color: "#fff",
    padding: "8px 16px 8px 16px",
    gap: "4px",
    "&:hover": {
      background: "linear-gradient(90deg, #17A9E0 0%, #0A88BF 100%)",
    },
    "&.Mui-disabled": {
      textWarp: "nowrap",
      color: "#B0B0B0",
      background: "#E7E7E7",
      boxShadow: "none",
    },
  },
}));

const StyledButtonDisableGradient = styled(Button)(({ theme }) => ({
  ...basicButtonProps,
  "&.MuiButton-root": {
    textWrap: "nowrap",
    color: "#B0B0B0",
    backgroundColor: "#E7E7E7",
    borderRadius: "6px",
    padding: "8px 16px 8px 16px",
    gap: "4px",
  },
}));

//
const ButtonMain = ({ badgeProps, buttonProps }) => {
  // style
  const classes = useStyles();
  // props
  const { text, className, variant, startIcon, ...buttonPropsRest } =
    buttonProps;
  const { badgeContent, ...badgePropRest } = badgeProps;
  // local state
  const handleShowBadge = () =>
    badgeContent !== undefined && badgeContent !== null;

  const getProps = () => ({
    ...buttonPropsRest,
    startIcon,
    className: classNames(className),
  });

  const getLoadingBtnProps = () => ({
    ...buttonPropsRest,
    loadingPosition: startIcon ? "start" : "center",
    startIcon,
    className: classNames(className),
  });

  const getCustomButton = () => {
    let CustomButton;
    let customProps = getProps();

    switch (variant) {
      case "clear":
        CustomButton = StyledButtonClear;
        break;
      case "contained":
        CustomButton = StyledButtonContained;
        break;
      case "gradient":
        CustomButton = StyledButtonGradient;
        break;
      case "linearGradient":
        CustomButton = StyledButtonLinearGradient;
        break;
      case "disableLinearGradient":
        CustomButton = StyledButtonDisableGradient;
        break;
      case "neutral":
        CustomButton = StyledButtonNeutral;
        break;
      case "neutralNoHover":
        CustomButton = StyledButtonNeutralNoHover;
        break;
      case "outlined":
        CustomButton = StyledButtonOutlined;
        break;
      case "text":
        CustomButton = StyledButtonText;
        break;
      case "hoverText":
        CustomButton = StyledButtonHoverText;
        break;
      case "disabled":
        CustomButton = StyledButtonDisabled;
        break;
      case "loading":
        CustomButton = StyledButtonLoading;
        // LoadingButton 的 props 客製化
        customProps = getLoadingBtnProps();
        break;
      case "feedback":
        CustomButton = StyledButtonFeedback;
        break;
      default:
        CustomButton = StyledButtonGradient;
    }
    return (
      (handleShowBadge() && (
        <StyledBadge badgeContent={badgeContent} {...badgePropRest}>
          <CustomButton {...customProps}>{text}</CustomButton>
        </StyledBadge>
      )) || <CustomButton {...customProps}>{text}</CustomButton>
    );
  };

  return getCustomButton();
};

ButtonMain.propTypes = {
  badgeProps: PropTypes.objectOf(PropTypes.any),
  buttonProps: PropTypes.objectOf(PropTypes.any),
};

ButtonMain.defaultProps = {
  badgeProps: {},
  buttonProps: {},
};

export default ButtonMain;
